import { FiArchive, FiCode, FiDownload, FiFile, FiFileText, FiImage } from "react-icons/fi";
import { formatFileSize } from "../config";

interface FilePreviewProps {
  /** File URL */
  url: string;
  /** Filename for display */
  filename: string;
  /** MIME type for icon selection */
  mimeType?: string | undefined;
  /** File size in bytes */
  size?: number | undefined;
  /** Preview variant */
  variant: "inline" | "full";
  /** Optional className */
  className?: string | undefined;
}

/**
 * Get appropriate icon based on MIME type
 */
function getFileIcon(mimeType?: string, filename?: string) {
  if (!mimeType && !filename) return FiFile;

  // Check by MIME type first
  if (mimeType) {
    if (mimeType.startsWith("image/")) return FiImage;
    if (mimeType.includes("pdf")) return FiFileText;
    if (
      mimeType.includes("zip") ||
      mimeType.includes("archive") ||
      mimeType.includes("compressed")
    ) {
      return FiArchive;
    }
    if (mimeType.includes("json") || mimeType.includes("javascript") || mimeType.includes("xml")) {
      return FiCode;
    }
    if (mimeType.includes("text") || mimeType.includes("document")) {
      return FiFileText;
    }
  }

  // Fallback to extension
  if (filename) {
    const ext = filename.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
      case "doc":
      case "docx":
      case "txt":
      case "rtf":
        return FiFileText;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
        return FiArchive;
      case "js":
      case "ts":
      case "jsx":
      case "tsx":
      case "json":
      case "xml":
      case "html":
      case "css":
        return FiCode;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
      case "svg":
      case "webp":
        return FiImage;
      default:
        return FiFile;
    }
  }

  return FiFile;
}

/**
 * FilePreview - File icon and download
 *
 * Inline: Icon with filename for table rows
 * Full: Larger icon with download button for modals
 */
export function FilePreview({
  url,
  filename,
  mimeType,
  size,
  variant,
  className = "",
}: FilePreviewProps) {
  const FileIcon = getFileIcon(mimeType, filename);

  if (variant === "inline") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gray-100">
          <FileIcon className="h-5 w-5 text-gray-600" />
        </div>
        <p className="truncate text-sm font-medium text-gray-900">{filename}</p>
      </div>
    );
  }

  // Full variant for modals
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 py-12">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-200">
          <FileIcon className="h-10 w-10 text-gray-600" />
        </div>
        <p className="mt-4 font-medium text-gray-900">{filename}</p>
        {size && <p className="mt-1 text-sm text-gray-500">{formatFileSize(size)}</p>}
      </div>

      <a
        href={url}
        download={filename}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
      >
        <FiDownload className="h-4 w-4" />
        Download
      </a>
    </div>
  );
}

export default FilePreview;
